/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { LuisAppTrainButton } from 'src/luis/appShell/appPage/components/LuisAppTrainButton';
import { ConversationPublishSlotStoreData } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { usePatternStores } from 'src/luis/modules/patterns/usePatternStores';
import { PublishModal } from 'src/luis/projects/publishPane/components/PublishModal';
import { TestPane } from 'src/luis/projects/testPane/components/TestPane';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';
import { HeaderNavigationLink } from 'src/modules/subHeader/components/HeaderNavigationLink';

const messages = defineMessages({
    dashboard: {
        id: 'luis.conversationAppHeaderItems.dashboard',
        defaultMessage: 'Dashboard'
    },
    build: {
        id: 'luis.conversationAppHeaderItems.build',
        defaultMessage: 'Build'
    },
    manage: {
        id: 'luis.conversationAppHeaderItems.manage',
        defaultMessage: 'Manage'
    },
    publish: {
        id: 'luis.conversationAppHeaderItems.publish',
        defaultMessage: 'Publish'
    },
    publishToastStart: {
        id: 'luis.conversationAppHeaderItems.publishToastStart',
        defaultMessage: "Publishing app '{ name }' ..."
    },
    publishToastSuccess: {
        id: 'luis.conversationAppHeaderItems.publishToastSuccess',
        defaultMessage: "Publish app '{ name }' completed"
    },
    publishToastFailed: {
        id: 'luis.conversationAppHeaderItems.publishToastFailed',
        defaultMessage: "Publish app '{ name }' failed"
    },
    accessUrlsButton: {
        id: 'luis.conversationAppHeaderItems.accessUrlsButton',
        defaultMessage: 'Access your endpoint Urls'
    }
});

export const LuisAppHeaderItems = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { rootStore, dispatcher } = useGlobalState();
    const { patternStores, arePatternStoresLoading } = usePatternStores();

    const [isPublishModalHidden, setIsPublishModalHidden] = React.useState(true);

    const currentApp = rootStore.currentLuisAppStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canDeployProject } = mapUserRoleToUserActions(userRole);
    const versionId = currentApp.activeVersion;
    const currentVersion = currentApp?.currentAppVersionStore;
    const isTrainingInProgress = currentVersion?.trainingStore.serviceStatus !== 'ready';

    const isDeployButtonDisabled = !currentVersion.isTrained || isTrainingInProgress;
    const navigationItems = isXLargeOrGreater ? (
        <>
            <HeaderNavigationLink to={routeTable.luis.dashboardPage(currentApp.id, versionId)}>
                {localize(messages.dashboard)}
            </HeaderNavigationLink>

            <HeaderNavigationLink to={routeTable.luis.buildPage(currentApp.id, versionId)}>{localize(messages.build)}</HeaderNavigationLink>

            <HeaderNavigationLink to={routeTable.luis.managePage(currentApp.id, versionId)}>
                {localize(messages.manage)}
            </HeaderNavigationLink>
        </>
    ) : null;

    /**
     * The train button should be disabled if training is in progress
     * or there is an intent that has no utterances and has no patterns.
     */
    const isTrainButtonDisabled = () => {
        if (isTrainingInProgress) {
            return true;
        }

        if (!currentVersion.intentStoreHouse.isLoaded || arePatternStoresLoading) {
            return true;
        }

        const isIntentNonTrainable = (i: IntentStore) => i.labelsCount === 0 && !patternStores.some(p => p.intentId === i.id);
        const intentStores = currentVersion.intentStoreHouse.items.filter(
            intentStore => intentStore.name !== LuisConstants.Luis.NoneIntentName
        );

        if (intentStores.length === 0) {
            return true;
        }

        return intentStores.some(isIntentNonTrainable);
    };

    /**
     * Publishes the application.
     *
     * @param settings The settings to use to publish the app.
     */
    const onPublish = (settings: ConversationPublishSlotStoreData): void => {
        dispatcher.dispatch('updatePublishSettings', {
            isStaging: settings.isStaging,
            isSentimentAnalysisOn: settings.isSentimentAnalysisOn,
            isSpeechPrimingOn: settings.isSpeechPrimingOn
        });

        dispatcher.dispatch('publishApp', {
            isStaging: settings.isStaging,
            toastData: {
                running: { title: localize(messages.publishToastStart, { name: currentApp.name }) },
                completed: {
                    title: localize(messages.publishToastSuccess, { name: currentApp.name }),
                    actions: [
                        {
                            label: localize(messages.accessUrlsButton),
                            onClick: () => history.push(routeTable.luis.azureResourcesPage(currentApp.id, currentVersion.id))
                        }
                    ]
                },
                failed: { title: localize(messages.publishToastFailed, { name: currentApp.name }) }
            }
        });
    };

    /**
     * Notifies the parent that the publish button was clicked.
     *
     * @param settings The settings to pass to the parent callback.
     */
    const onPublishButtonClick = (settings: ConversationPublishSlotStoreData): void => {
        onPublish(settings);
        setIsPublishModalHidden(true);
    };

    return (
        <Stack horizontal grow horizontalAlign="end" verticalFill tokens={{ childrenGap: 26 }}>
            <Stack horizontal grow horizontalAlign="end" verticalFill>
                {navigationItems}
            </Stack>

            <Stack horizontal horizontalAlign="end" verticalFill verticalAlign="center" tokens={{ childrenGap: 12 }}>
                <LuisAppTrainButton
                    onTrain={() => dispatcher.dispatch('train')}
                    isTrainingInProgress={isTrainingInProgress}
                    isVersionDirty={currentVersion.isDirty}
                    isDisabled={isTrainButtonDisabled() || !canWrite}
                />

                <TestPane />

                <RBACTooltip
                    hidden={canDeployProject || isDeployButtonDisabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="publishButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <DefaultButton
                            data-automation-id="publishButton"
                            iconProps={{ iconName: 'Deploy' }}
                            onClick={() => setIsPublishModalHidden(false)}
                            disabled={isDeployButtonDisabled || !canDeployProject}>
                            {localize(messages.publish)}
                        </DefaultButton>
                    }
                />
            </Stack>

            <PublishModal
                hidden={isPublishModalHidden}
                onDismiss={() => setIsPublishModalHidden(true)}
                productionData={currentApp.publishSlots.production as ConversationPublishSlotStoreData}
                stagingData={currentApp.publishSlots.staging as ConversationPublishSlotStoreData}
                onSubmit={onPublishButtonClick}
            />
        </Stack>
    );
});
